// ABOUTME: Component that handles invitation acceptance flow from email links
// ABOUTME: Displays invitation details and handles account creation/signup

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Container
} from '@mui/material';
import { Person, Email, Business } from '@mui/icons-material';
import PolicyAcceptanceModal from './PolicyAcceptanceModal';
import { getPrivacyPolicy, getTermsOfService, acknowledgePolicyDocument, authAPI, setCurrentAccessToken } from '../services/api';
import type { PolicyDocument } from '../types/api';

interface InvitationDetails {
  id: number;
  familyName: string;
  invitedByName: string;
  email: string;
  role: string;
  customRole?: string;
  message?: string;
  firstName?: string;
  lastName?: string;
  status: string;
  invitedAt: string;
  expiresAt: string;
}

interface SignupFormData {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

const InvitationAcceptance: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [familyInfo, setFamilyInfo] = useState<{ familyName: string; invitedByName: string } | null>(null);

  const [signupForm, setSignupForm] = useState<SignupFormData>({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policies, setPolicies] = useState<{
    privacy: PolicyDocument | null;
    terms: PolicyDocument | null;
  }>({ privacy: null, terms: null });

  // Load invitation details
  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/invitations/token/${token}`);

        if (response.ok) {
          const data = await response.json();
          setInvitation(data);
          setSignupForm(prev => ({
            ...prev,
            firstName: data.firstName || '',
            lastName: data.lastName || ''
          }));
        } else if (response.status === 410) {
          // Expired invitation
          const data = await response.json();
          setExpired(true);
          setFamilyInfo({
            familyName: data.familyName || 'Unknown Family',
            invitedByName: data.invitedByName || 'Someone'
          });
        } else if (response.status === 404) {
          setError('This invitation link is invalid or has already been used');
        } else {
          const data = await response.json();
          if (data.error?.includes('already been used')) {
            setError('This invitation link is invalid or has already been used');
          } else {
            setError('Failed to load invitation details');
          }
        }
      } catch {
        setError('Failed to load invitation details');
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  const handleInputChange = (field: keyof SignupFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSignupForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateForm = (): boolean => {
    if (!signupForm.firstName.trim()) {
      setSignupError('First name is required');
      return false;
    }
    if (!signupForm.lastName.trim()) {
      setSignupError('Last name is required');
      return false;
    }
    if (signupForm.password.length < 8) {
      setSignupError('Password must be at least 8 characters');
      return false;
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      setSignupError('Passwords do not match');
      return false;
    }
    return true;
  };

  // Performs the actual account creation and invitation acceptance after policies are accepted.
  // Called directly by onAccepted to avoid stale-closure issues with policiesAccepted state.
  const performSignup = async (acceptedPolicies: { privacy: PolicyDocument; terms: PolicyDocument }) => {
    if (!invitation) return;

    setSignupLoading(true);
    setSignupError(null);

    try {
      // Create account
      const signupResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: invitation.email,
          password: signupForm.password,
          firstName: signupForm.firstName,
          lastName: signupForm.lastName
        }),
      });

      if (!signupResponse.ok) {
        const errorData = await signupResponse.json();
        if (signupResponse.status === 409) {
          setSignupError('This invitation was sent to an email that already has a Let\'s Reset account. Please contact support@letsreset.ai for help.');
        } else {
          setSignupError(errorData.message || 'Failed to create account');
        }
        return;
      }

      const authData = await signupResponse.json();

      // Store auth state — access token in module variable, user profile in localStorage
      setCurrentAccessToken(authData.token);
      authAPI.setAuth(authData);

      // Acknowledge policies now that we have an auth token
      try {
        await Promise.all([
          acknowledgePolicyDocument({ documentType: 'PrivacyPolicy', documentVersion: acceptedPolicies.privacy.version }),
          acknowledgePolicyDocument({ documentType: 'TermsOfService', documentVersion: acceptedPolicies.terms.version }),
        ]);
      } catch (policyError) {
        console.error('Failed to record policy acknowledgments:', policyError);
        // Don't fail signup if policy acknowledgment fails
      }

      // Accept invitation
      const acceptResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData.token}`
        },
        body: JSON.stringify({
          token: token
        }),
      });

      if (!acceptResponse.ok) {
        const errorData = await acceptResponse.json();
        setSignupError(errorData.message || 'Failed to accept invitation');
        return;
      }

      // Force page reload to update auth context, then redirect to onboarding
      window.location.href = '/onboarding/invited';
    } catch {
      setSignupError('An unexpected error occurred');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!validateForm()) return;
    if (!invitation) return;

    // Load policies and show acceptance modal before creating account
    setSignupLoading(true);
    let loadedPolicies: { privacy: PolicyDocument; terms: PolicyDocument };
    try {
      const [privacy, terms] = await Promise.all([getPrivacyPolicy(), getTermsOfService()]);
      loadedPolicies = { privacy, terms };
      setPolicies(loadedPolicies);
    } catch {
      setSignupError('Failed to load policies. Please try again.');
      setSignupLoading(false);
      return;
    }
    setSignupLoading(false);
    setShowPolicyModal(true);
  };

  const handleSocialAuth = (provider: string) => {
    // For now, show a notice about email verification
    alert(`Please ensure you sign up with ${invitation?.email} when using ${provider}`);
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box mt={4}>
          <Card>
            <CardContent>
              <Typography variant="h5" color="error" gutterBottom>
                Invitation Error
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (expired && familyInfo) {
    return (
      <Container maxWidth="sm">
        <Box mt={4}>
          <Card>
            <CardContent>
              <Typography variant="h5" color="warning.main" gutterBottom>
                Invitation Expired
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                This invitation has expired.
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Please contact <strong>{familyInfo.invitedByName}</strong> from <strong>{familyInfo.familyName}</strong> to request a new invitation.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <Container maxWidth="sm">
      <Box mt={4}>
        <Card>
          <CardContent>
            <Box textAlign="center" mb={3}>
              <Typography variant="h4" gutterBottom>
                You&apos;re Invited!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Join your family on Let&apos;s Reset
              </Typography>
            </Box>

            {/* Invitation Details */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" mb={2}>
                <Business sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">{invitation.familyName}</Typography>
              </Box>

              <Box display="flex" alignItems="center" mb={2}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  Invited by {invitation.invitedByName}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" mb={2}>
                <Email sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">{invitation.email}</Typography>
              </Box>

              {invitation.customRole && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Role: {invitation.customRole}
                </Typography>
              )}

              {invitation.message && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {invitation.message}
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Signup Form */}
            <Typography variant="h6" gutterBottom>
              Create Your Account
            </Typography>

            {signupError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {signupError}
              </Alert>
            )}

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSignup(); }}>
              <TextField
                fullWidth
                label="First Name"
                value={signupForm.firstName}
                onChange={handleInputChange('firstName')}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Last Name"
                value={signupForm.lastName}
                onChange={handleInputChange('lastName')}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                type="password"
                label="Password"
                value={signupForm.password}
                onChange={handleInputChange('password')}
                margin="normal"
                required
                helperText="Minimum 8 characters"
              />

              <TextField
                fullWidth
                type="password"
                label="Confirm Password"
                value={signupForm.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                margin="normal"
                required
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={signupLoading}
                sx={{ mt: 3, mb: 2 }}
              >
                {signupLoading ? <CircularProgress size={24} /> : 'Sign up and join'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }}>or</Divider>

            {/* Social Auth Options */}
            <Box display="flex" flexDirection="column" gap={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleSocialAuth('Google')}
                disabled={signupLoading}
              >
                Sign up with Google
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleSocialAuth('Apple')}
                disabled={signupLoading}
              >
                Sign up with Apple
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              When using social sign-up, you must use {invitation.email}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <PolicyAcceptanceModal
        open={showPolicyModal}
        onAccepted={() => {
          setShowPolicyModal(false);
          if (policies.privacy && policies.terms) {
            performSignup({ privacy: policies.privacy, terms: policies.terms });
          }
        }}
        onClose={() => setShowPolicyModal(false)}
        skipApiCall={true}
      />
    </Container>
  );
};

export default InvitationAcceptance;